import { useState, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import type { NavItem } from "./nav";

interface Props {
  item: NavItem;
  collapsed?: boolean;
  prefetch?: (to: string) => Promise<unknown> | void;
}

export function SidebarLink({ item, collapsed, prefetch }: Props) {
  const location = useLocation();
  const qc = useQueryClient();
  const hasChildren = !!item.children?.length;

  // Open submenu by default if any child is currently active.
  const childActive =
    hasChildren && item.children!.some((c) => c.to && location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(childActive);

  const onMouseEnter = useCallback(() => {
    if (item.to && prefetch) prefetch(item.to);
    if (hasChildren) {
      item.children!.forEach((c) => c.to && prefetch?.(c.to));
    }
  }, [item, prefetch, hasChildren]);

  const Icon = item.icon;

  /* ─── Leaf (no children) ───────────────────────────────────────── */
  if (!hasChildren && item.to) {
    return (
      <NavLink
        to={item.to}
        onMouseEnter={onMouseEnter}
        className={({ isActive }) =>
          cn(
            "group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
            isActive
              ? "bg-primary/15 text-primary"
              : "text-fg-muted hover:text-fg hover:bg-bg-elevated/60"
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <motion.span
                layoutId="sidebar-active"
                className="absolute inset-y-1 left-0 w-0.5 rounded-r bg-primary"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            {Icon && <Icon className="h-[18px] w-[18px] shrink-0" />}
            {!collapsed && <span className="truncate">{item.label}</span>}
            {!collapsed && item.badge && (
              <span className="ml-auto rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-1.5 py-0.5">
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  }

  /* ─── Branch (with children) ───────────────────────────────────── */
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={onMouseEnter}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
          childActive
            ? "text-fg"
            : "text-fg-muted hover:text-fg hover:bg-bg-elevated/60"
        )}
        aria-expanded={open}
      >
        {Icon && <Icon className="h-[18px] w-[18px] shrink-0" />}
        {!collapsed && (
          <>
            <span className="truncate text-left flex-1">{item.label}</span>
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                open && "rotate-90 text-primary"
              )}
            />
          </>
        )}
      </button>
      {!collapsed && (
        <AnimatePresence initial={false}>
          {open && (
            <motion.ul
              key="sub"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18, ease: [0.21, 1, 0.21, 1] }}
              className="overflow-hidden"
            >
              <li className="ml-3 my-1 border-l border-border/60 pl-3">
                <ul className="space-y-0.5">
                  {item.children!.map((c) => (
                    <li key={c.label}>
                      {c.to && (
                        <NavLink
                          to={c.to}
                          onMouseEnter={() => prefetch?.(c.to!)}
                          className={({ isActive }) =>
                            cn(
                              "block rounded-md px-2.5 py-1.5 text-[13px] transition-colors",
                              isActive
                                ? "text-primary font-medium"
                                : "text-fg-subtle hover:text-fg hover:bg-bg-elevated/60"
                            )
                          }
                        >
                          <span className="inline-flex items-center gap-2">
                            <span className="h-1 w-1 rounded-full bg-current opacity-60" />
                            {c.label}
                          </span>
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            </motion.ul>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
